import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {trackerMockDataSubFields, popupMockDataSubFields, tpaBalanceMockData} from './utils'
import {isEditing, isPreviewing} from '@builder.io/sdk-react'
import {TpaBalance} from '@/components/tpa-balance'

const mergeMockDataWithDefaults = (mockData) => ({
    balance: {
        tracker: {
            ...tpaBalanceMockData.balance.tracker,
            ...mockData?.balance?.tracker
        },
        popup: mockData?.balance?.popup?.length
            ? mockData.balance.popup
            : tpaBalanceMockData.balance.popup
    },
    popup: {
        ...tpaBalanceMockData.popup,
        ...mockData?.popup,
        marketingConsentCheckbox: {
            ...tpaBalanceMockData.popup.marketingConsentCheckbox,
            ...mockData?.popup?.marketingConsentCheckbox
        }
    }
})

function BuilderTpaBalance({
    balancePopupText,
    useMockData,
    mockData,
    bdLogo,
    welcomeLogo,
    attributes: {className, ...attributes}
}) {
    const isEdit = isEditing()
    const isPreview = isPreviewing()

    const resolvedMockData = useMemo(
        () => (useMockData ? mergeMockDataWithDefaults(mockData) : mockData),
        [useMockData, mockData]
    )

    return (
        <TpaBalance
            balancePopupText={balancePopupText}
            useMockData={useMockData}
            bdLogo={bdLogo}
            welcomeLogo={welcomeLogo}
            mockData={resolvedMockData}
            isEdit={isEdit}
            isPreview={isPreview}
            className={className}
            {...attributes}
            data-testid="tpaBalance-TpaBalance"
        />
    )
}

BuilderTpaBalance.propTypes = {
    attributes: PropTypes.object,
    balancePopupText: PropTypes.string,
    useMockData: PropTypes.bool,
    mockData: PropTypes.object,
    bdLogo: PropTypes.object,
    welcomeLogo: PropTypes.object
}

export const TpaBalanceDefinition = {
    component: BuilderTpaBalance,
    name: 'TPABalance',
    noWrap: true,
    inputs: [
        {
            name: 'balancePopupText',
            type: 'longText',
            required: true,
            defaultValue:
                'All of your accounts are now in one place with BalanceDisplay™. The easier way to keep track of your balances and end of year purchases.'
        },
        {name: 'useMockData', type: 'boolean', required: true, defaultValue: false},
        {
            name: 'bdLogo',
            type: 'object',
            required: false,
            subFields: [
                {
                    name: 'src',
                    type: 'file',
                    allowedFileTypes: ['png', 'jpg', 'jpeg', 'svg'],
                    required: false
                },
                {name: 'altText', type: 'text', required: false}
            ]
        },
        {
            name: 'welcomeLogo',
            type: 'object',
            required: false,
            subFields: [
                {
                    name: 'src',
                    type: 'file',
                    allowedFileTypes: ['png', 'jpg', 'jpeg', 'svg'],
                    required: false
                },
                {name: 'altText', type: 'text', required: false}
            ]
        },
        {
            name: 'mockData',
            type: 'object',
            showIf: `options.get('useMockData') === true`,
            defaultValue: tpaBalanceMockData,
            subFields: [
                {
                    name: 'balance',
                    type: 'object',
                    required: true,
                    subFields: trackerMockDataSubFields
                },
                {
                    name: 'popup',
                    type: 'object',
                    required: true,
                    subFields: popupMockDataSubFields
                }
            ]
        }
    ]
}
